/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { initializeDestinationCookies } from '@/destinations/cookie_initializer';
import { Logger } from '@/shared/logger/logger';

import {
  ConversionValueData,
  TEventSpecificData,
  TMableEvent,
} from '../constants/event_types/base_mable_event';
import {
  CheckoutStartedEvent,
  CollectionViewedEvent,
  CustomMableEvent,
  EMableEventName,
  LeadEvent,
  OrderCompletedEvent,
  PageViewedEvent,
  PaymentInfoAddedEvent,
  ProductAddedEvent,
  ProductViewedEvent,
} from '../constants/event_types/events';
import {
  TCartData,
  TMableProduct,
} from '../constants/event_types/extended_mable_event';
import { EDestinationType } from '../interfaces';

export function composeFinalMableEvent(
  eventName: EMableEventName,
  eventSpecificData: TEventSpecificData,
  baseMableEvent: TMableEvent,
): TMableEvent {
  baseMableEvent.en = eventName;
  const baseEvent = { ...baseMableEvent };

  switch (eventName) {
    case EMableEventName.PRODUCT_VIEWED: {
      return {
        ...baseEvent,
        esd: eventSpecificData as {
          act_product: TMableProduct;
          cv_data: ConversionValueData;
        },
      } as ProductViewedEvent;
    }
    case EMableEventName.COLLECTION_VIEWED: {
      return {
        ...baseEvent,
        esd: eventSpecificData as {
          act_product: TMableProduct;
          cv_data: ConversionValueData;
        },
      } as CollectionViewedEvent;
    }
    case EMableEventName.PRODUCT_ADDED: {
      return {
        ...baseEvent,
        esd: eventSpecificData as {
          act_product: TMableProduct;
          cart: TCartData;
          cv_data: ConversionValueData;
          qty_act_product: number;
        },
      } as ProductAddedEvent;
    }
    case EMableEventName.CHECKOUT_STARTED: {
      return {
        ...baseEvent,
        esd: eventSpecificData as {
          cart: TCartData;
          cv_data: ConversionValueData;
        },
      } as CheckoutStartedEvent;
    }
    case EMableEventName.PAYMENT_INFO_ADDED: {
      return {
        ...baseEvent,
        esd: eventSpecificData as {
          cart: TCartData;
          cv_data: ConversionValueData;
          pay_methods: string[];
        },
      } as PaymentInfoAddedEvent;
    }
    case EMableEventName.ORDER_COMPLETED: {
      eventSpecificData.pay_methods = eventSpecificData?.pay_methods || [];
      return {
        ...baseEvent,
        esd: eventSpecificData as {
          cart: TCartData;
          cv_data: ConversionValueData;
          oid: string;
          onb: string;
          tags: string[];
        },
      } as OrderCompletedEvent;
    }
    case EMableEventName.LEAD: {
      return {
        ...baseEvent,
        esd: {},
      } as LeadEvent;
    }
    case EMableEventName.PAGE_VIEWED: {
      return {
        ...baseEvent,
        esd: {},
      } as PageViewedEvent;
    }
    default: {
      return {
        ...baseEvent,
        en: 'Custom Event' as EMableEventName,
        esd: {
          ...eventSpecificData,
          act_product: eventSpecificData?.act_product,
          cart: eventSpecificData?.cart,
          cen: eventSpecificData?.cen || eventName,
        },
      } as CustomMableEvent;
    }
  }
}

export function validateCookies(
  cks: Record<string, string>,
  iqpars: Record<string, string[]>,
  timestamp: number,
  destinationTypes: EDestinationType[],
): Record<string, string> {
  const { newCookies } = initializeDestinationCookies(
    destinationTypes,
    cks,
    iqpars,
    timestamp,
  );
  return newCookies;
}

export function validateCustomEventData(
  eventName: EMableEventName,
  data: TEventSpecificData | any,
): TEventSpecificData {
  if (!data) return {};
  if (data?.cart?.total) {
    data.cart.total.price = Number(data.cart.total.price) || 0;
    data.cart.total.discount = Number(data.cart.total.discount) || 0;
    data.cart.total.shipping = Number(data.cart.total.shipping) || 0;
    data.cart.total.tax = Number(data.cart.total.tax) || 0;
    data.cart.products.forEach((product: TMableProduct) => {
      product.totalDiscount = Number(product.totalDiscount) || 0;
      // delete any null fields
      Object.entries(product).forEach(([key, value]) => {
        if (value === null) {
          delete (product as Partial<TMableProduct>)[
            key as keyof TMableProduct
          ];
        }
      });
    });
    data.cv_data = {
      cv: data.cart.total.price,
      discount: data.cart.total.discount,
      org_cv: data.cart.total.price,
      shipping: data.cart.total.shipping,
      tax: data.cart.total.tax,
    } as ConversionValueData;
  }
  switch (eventName) {
    case EMableEventName.PRODUCT_ADDED: {
      if (!data.act_product) Logger.warnMissingProperty('act_product');
      if (!data.cart) Logger.warnMissingProperty('cart');
      if (!data.qty_act_product) Logger.warnMissingProperty('qty_act_product');
      if (!data.cv_dta) Logger.warnMissingProperty('cv_data');
      return {
        act_product: data.act_product || {},
        cart: data.cart || {},
        cv_data: data.cv_data,
        qty_act_product: data.qty_act_product || 0,
      };
    }
    case EMableEventName.CHECKOUT_STARTED: {
      if (!data.cart) Logger.warnMissingProperty('cart');
      if (!data.cv_data) Logger.warnMissingProperty('cv_data');
      return { cart: data.cart, cv_data: data.cv_data };
    }
    case EMableEventName.PAYMENT_INFO_ADDED: {
      if (!data.cart) Logger.warnMissingProperty('cart');
      if (!data.pay_methods) Logger.warnMissingProperty('pay_methods');
      if (!data.cv_data) Logger.warnMissingProperty('cv_data');
      return {
        cart: data.cart,
        cv_data: data.cv_data,
        pay_methods: data.pay_methods,
      };
    }
    case EMableEventName.ORDER_COMPLETED: {
      if (!data.cart) Logger.warnMissingProperty('cart');
      if (!data.oid) Logger.warnMissingProperty('oid');
      if (!data.onb) Logger.warnMissingProperty('onb');
      if (!data.cv_data) Logger.warnMissingProperty('cv_data');
      return {
        cart: data.cart,
        cv_data: data.cv_data,
        oid: data.oid,
        onb: data.onb || '',
        pay_methods: data.pay_methods || '',
        tags: data.tags || [],
      };
    }
    case EMableEventName.PRODUCT_VIEWED:
    case EMableEventName.COLLECTION_VIEWED: {
      if (!data.act_product) Logger.warnMissingProperty('act_product');
      if (!data.cv_data) Logger.warnMissingProperty('cv_data');
      return { act_product: data.act_product, cv_data: data.cv_data };
    }
    case EMableEventName.LEAD:
    case EMableEventName.PAGE_VIEWED: {
      return {};
    }
    case EMableEventName.POST_PURCHASE_SURVEY: {
      if (!data.pps_channel) Logger.warnMissingProperty('pps_channel');
      return { pps_channel: data.pps_channel };
    }
    default: {
      if (!data?.cen) {
        Logger.warn("Custom event must have a 'cen' property");
      }
      return {
        act_product: data?.act_product,
        cart: data?.cart,
        cen: data?.cen || eventName,
        qty_act_product: data?.qty_act_product,
      };
    }
  }
}
